import { useState, useMemo, useEffect, useCallback } from "react"
import { useTenant } from "../../../hooks/useTenant"
import LevelService from "../../../services/level_service"
import EventService from "../../../services/event_service"
import MemberService from "../../../services/member_service"
import { useClassMembers } from "./useClassMembers"
import { useDebounce } from "../../../hooks/useDebounce"

/**
 * useEvaluationFormLogic
 * Lógica central do formulário de avaliação.
 * Usa loading simples com useState (padrão do projeto), busca levels do LevelService,
 * eventos do EventService com filtro por tipo/data, e membros do MemberService.
 */
export const useEvaluationFormLogic = ({ classId, activeMode = "technical" }) => {
    const { idTenant, user } = useTenant()

    // Loading simples (padrão NeoApp: useState)
    // Inicializa com keys críticas como true para evitar flash no primeiro frame
    const [loadingKeys, setLoadingKeys] = useState({
        checkEvent: true,
        classMembers: true,
        objectives: true
    })
    const [extraMembers, setExtraMembers] = useState([])
    const [searchText, setSearchText] = useState("")
    const [excludedMemberIds, setExcludedMemberIds] = useState(() => new Set())
    const [activeEvent, setActiveEvent] = useState(null)
    const [activeTestEvent, setActiveTestEvent] = useState(null)
    const [levels, setLevels] = useState([])
    const [allMembers, setAllMembers] = useState([])

    const debouncedSearch = useDebounce(searchText, 400)

    // Reset view specific states when class changes
    useEffect(() => {
        setExtraMembers([])
        setSearchText("")
        setExcludedMemberIds(new Set())
    }, [classId])

    // Loading helpers (copia o padrão do ContentLoader)
    const isLoading = useCallback((key) => {
        if (!key) return Object.values(loadingKeys).some(Boolean)
        return !!loadingKeys[key]
    }, [loadingKeys])
    const anyLoading = Object.values(loadingKeys).some(Boolean)

    const withLoading = useCallback(async (key, fn) => {
        setLoadingKeys(prev => ({ ...prev, [key]: true }))
        try {
            return await fn()
        } finally {
            setLoadingKeys(prev => ({ ...prev, [key]: false }))
        }
    }, [])

    // Buscar níveis do LevelService
    useEffect(() => {
        if (!idTenant) return
        const loadLevels = async () => {
            try {
                const result = await LevelService.getLevels(idTenant)
                setLevels(result || [])
            } catch (e) {
                console.error("Erro ao carregar níveis:", e)
            }
        }
        loadLevels()
    }, [idTenant])

    // Buscar todos os membros (para pool de adição)
    useEffect(() => {
        if (!idTenant) return
        const loadMembers = async () => {
            try {
                const result = await MemberService.getMembers(idTenant)
                setAllMembers(result || [])
            } catch (e) {
                console.error("Erro ao carregar membros:", e)
            }
        }
        loadMembers()
    }, [idTenant])

    // Buscar membros da turma selecionada
    const { members: classMembers } = useClassMembers({ classId, withLoading })

    // Verificar evento ativo de avaliação e teste
    // Filtra eventos por tipo e verifica se está no prazo
    useEffect(() => {
        if (!idTenant) return

        const checkEvents = async () => {
            try {
                await withLoading('checkEvent', async () => {
                    const allEvents = await EventService.getAll(idTenant)
                    const now = new Date()

                    // Filtrar eventos ativos por tipo e dentro do prazo
                    const findActiveEvent = (type) => {
                        return allEvents.find(evt => {
                            if (evt.type !== type) return false
                            if (evt.status === 'inactive' || evt.status === 'closed') return false
                            // Verifica se está dentro do prazo
                            const startDate = evt.startDate ? new Date(evt.startDate) : null
                            const endDate = evt.endDate ? new Date(evt.endDate) : null
                            if (startDate && now < startDate) return false
                            if (endDate) {
                                const endOfDay = new Date(endDate)
                                endOfDay.setHours(23, 59, 59, 999)
                                if (now > endOfDay) return false
                            }
                            return true
                        }) || null
                    }

                    setActiveEvent(findActiveEvent('evaluation'))
                    setActiveTestEvent(findActiveEvent('test'))
                })
            } catch (e) {
                console.error("Erro ao verificar evento ativo", e)
            }
        }
        checkEvents()
    }, [idTenant, withLoading])

    // Mix de clientes: turma + adicionados manualmente
    const combinedMembers = useMemo(() => {
        const base = Array.isArray(classMembers) ? classMembers : []
        const extra = Array.isArray(extraMembers) ? extraMembers : []
        const map = new Map()
        base.forEach(s => {
            if (s?.id == null) return
            map.set(String(s.id), s)
        })
        extra.forEach(s => {
            if (s?.id == null) return
            const key = String(s.id)
            if (!map.has(key)) map.set(key, s)
        })
        return Array.from(map.values())
    }, [classMembers, extraMembers])

    // Clientes visíveis (sem os excluídos)
    const evaluationMembers = useMemo(() => {
        const base = Array.isArray(combinedMembers) ? combinedMembers : []
        if (!excludedMemberIds || excludedMemberIds.size === 0) return base
        return base.filter(s => !excludedMemberIds.has(String(s.id)))
    }, [combinedMembers, excludedMemberIds])

    // Candidatos para adicionar (buscando no pool de membros com debounce)
    const addCandidates = useMemo(() => {
        const q = (debouncedSearch || "").trim().toLowerCase()
        if (!q || q.length < 2) return []
        const existingIds = new Set((combinedMembers || []).map(s => String(s.id)))
        return (allMembers || [])
            .filter(m => !existingIds.has(String(m.id)))
            .filter(m => {
                const name = (m.fullName || m.name || "").toLowerCase()
                return name.includes(q)
            })
            .map(m => ({
                id: m.id,
                idMember: m.id,
                name: m.fullName || m.name || "Membro",
                photo: m.photoUrl || null,
                phone: m.phone || null,
                email: m.email || null,
                memberNumber: m.memberNumber || "",
            }))
            .slice(0, 8)
    }, [allMembers, combinedMembers, debouncedSearch])

    const showNoAutocompleteResults = useMemo(() => {
        const q = (debouncedSearch || "").trim()
        if (!q || q.length < 2) return false
        return addCandidates.length === 0
    }, [debouncedSearch, addCandidates.length])

    const defaultLevelId = useMemo(() => {
        const first = Array.isArray(levels) && levels.length > 0 ? levels[0] : null
        return first?.id != null ? String(first.id) : ""
    }, [levels])

    const toggleExcludeMember = (idMember) => {
        const key = String(idMember)
        setExcludedMemberIds(prev => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    const handleAddMember = (member) => {
        const sId = String(member.id)
        setExtraMembers(prev => {
            const next = Array.isArray(prev) ? prev.slice() : []
            if (!next.some(x => String(x.id) === sId)) next.push(member)
            return next
        })
        setExcludedMemberIds(prev => {
            const next = new Set(prev)
            next.delete(sId)
            return next
        })
        setSearchText("")
    }

    // formReady: só true quando TODOS os dados críticos terminaram de carregar
    const requiresObjectives = activeMode === 'technical'
    const formReady = !loadingKeys.checkEvent && !loadingKeys.classMembers && (!requiresObjectives || !loadingKeys.objectives)

    return {
        isLoading,
        anyLoading,
        formReady,
        withLoading,
        searchText,
        setSearchText,
        levels,
        activeEvent,
        activeTestEvent,
        allMembers: combinedMembers,
        evaluationMembers,
        addCandidates,
        showNoAutocompleteResults,
        defaultLevelId,
        toggleExcludeMember,
        handleAddMember,
        excludedMemberIds,
        classMembers,
        idTenant,
        user
    }
}
